import { defineStore } from "pinia";
import { computed, ComputedRef } from "vue";
import { Store, useStore } from "vuex";

import { isAtLeastRoleEditor, isAtLeastRoleUser, isExactRoleOrganizationAdmin } from "../utils/roles";
import { useFeaturesStore } from "./features";

type TUserInterfaceAccess = {
  all?: boolean;
  create?: boolean;
  delete?: boolean;
  read?: boolean;
  update?: boolean;
};

export const useUserRoleAccessStore = defineStore("userRoleAccess", () => {
  const store: Store<any> = useStore();
  const { state } = useFeaturesStore();
  const ENABLE = true;
  const DISABLE = false;

  const playlistAccess: ComputedRef<TUserInterfaceAccess> = computed(() => {
    if ((state.features.Playlists_AllowUsersToManage && isAtLeastRoleUser()) || isAtLeastRoleEditor()) {
      return setAccess({ all: ENABLE });
    }
    return setAccess({ all: DISABLE });
  });

  const meetingSalesforceRemapAccess: ComputedRef<
    // eslint-disable-next-line no-unused-vars
    (options: { meetingHostUserId: number }) => TUserInterfaceAccess
  > = computed(() => {
    return (options: { meetingHostUserId: number }) => {
      if (options?.meetingHostUserId === store.state?.authUser?.userId || isExactRoleOrganizationAdmin()) {
        return setAccess({ all: ENABLE });
      }
      return setAccess({ all: DISABLE });
    };
  });

  const accountsAccess: ComputedRef<TUserInterfaceAccess> = computed(() => {
    if (isExactRoleOrganizationAdmin() || state.features?.meetingAccessUnrestricted) {
      return setAccess({ all: ENABLE });
    }

    return setAccess({ all: DISABLE });
  });

  return {
    accountsAccess,
    meetingSalesforceRemapAccess,
    playlistAccess,
  };
});

function setAccess(options?: {
  all?: boolean;
  create?: boolean;
  deleteAccess?: boolean;
  read?: boolean;
  update?: boolean;
}): TUserInterfaceAccess {
  const { all, create, deleteAccess, read, update } = options;
  const access: TUserInterfaceAccess = {
    all: false,
    create: false,
    read: false,
    update: false,
  };

  if (all) {
    for (const key in access) {
      access[key as keyof TUserInterfaceAccess] = true;
    }
  } else {
    access.create = create ?? false;
    access.read = read ?? false;
    access.update = update ?? false;
    access.delete = deleteAccess ?? false;
  }

  return access;
}
